import React, { useEffect, useState, useRef } from "react";
import { Modal } from "components/modal";
import { ProgressSteps, PersonalInfoData, ConsultantProfileForm, CompanyInfoForm, ConsultantObjectivesForm, HRAgencyObjectivesForm, CompanyHRObjectivesForm, PersonalInfoForm } from "components/onboarding";
import { Rope, Onboarding } from "assets";

interface OnboardingModalProps {
  show: boolean;
  submitPersonal: (data: any) => void;
  submitBusiness: (data: any) => void;
  stage: number;
  setStage: React.Dispatch<React.SetStateAction<number>>;
  personalData: PersonalInfoData | undefined;
  countryOptions: { label: string; value: string }[];
  handleImageUpload: () => void;
  image: string | null;
  formImage?: string | null;
  existingLogoURL?: string | null;
  existingProfilePictureURL?: string | null;
  isSubmittingPersonal: boolean;
  isSubmittingBusiness: boolean;
}

export const OnboardingModalUI: React.FC<OnboardingModalProps> = ({
  show,
  submitPersonal,
  submitBusiness,
  stage,
  setStage,
  personalData,
  countryOptions,
  handleImageUpload,
  image,
  formImage,
  existingLogoURL,
  existingProfilePictureURL,
  isSubmittingPersonal,
  isSubmittingBusiness,
}) => {
  const [personal, setPersonal] = useState<PersonalInfoData>(
    personalData ?? { firstName: "", lastName: "", role: "", number: { code: "", number: "", value: "" }, how: { label: "", value: "" }, other: undefined, country: { label: "", value: "" } }
  );
  const [objectives, setObjectives] = useState<any>({});
  const [selectedRole, setSelectedRole] = useState<string>("");

  // Track if we've initialized from personalData to avoid overwriting user's in-session selections
  const hasInitializedFromPersonalData = useRef(false);

  useEffect(() => {
    // Only initialize from personalData once (on mount or when it first becomes available)
    // This prevents overwriting user's current session data when API updates come back
    if (personalData && !hasInitializedFromPersonalData.current) {
      setPersonal(personalData);
      setSelectedRole(personalData.role);
      hasInitializedFromPersonalData.current = true;
    }
  }, [personalData]);

  const goToStageTwo = (data: PersonalInfoData) => {
    setPersonal(data);
    setSelectedRole(data.role);
    const submitData = {
      first_name: data.firstName,
      last_name: data.lastName,
      referral: data.other !== "" ? data.other : data.how.value,
      role: data.role,
      country_code: data.number.code,
      phone_number: data.number.number,
      country: data.country.label,
    };
    submitPersonal(submitData);
    setStage(2);
    // Note: Stage transition is now handled in the success callback
  };

  const goToStageThree = (data: any) => {
    setObjectives(data);
    setStage(3);
  };

  const submitAll = (data: any) => {
    const submitData = {
      role: personal.role,
      first_name: personal.firstName,
      last_name: personal.lastName,
      phone_number: personal.number.number,
      referral: personal.other !== "" ? personal.other : personal.how.value,
      country_code: personal.number.code,
      country: personal.country.label,
      use_case: objectives.usage || [],
    };

    if (selectedRole === "hr_agency") {
      Object.assign(submitData, {
        industry_type: data.clientIndustries === "single_industry" ? "single" : "multiple",
        work_culture: objectives.clientValues,
        role_types: objectives.agencyFocus,
        handle_hiring: objectives.hrManagement || "",
        client_industries: objectives.clientIndustriesList && Array.isArray(objectives.clientIndustriesList) 
          ? objectives.clientIndustriesList 
          : [],
        name: data.name,
        industry: data.industry,
        hiring_frequency: data.frequency.value,
        no_of_employees: data.employees.value,
        ...(data.company_logo ? { company_logo: data.company_logo } : {}),
      });
    } else if (selectedRole === "independent_consultant") {
      Object.assign(submitData, {
        industry_type: data.industryFocus?.value === "single_industry" ? "single" : "multiple",
        work_culture: objectives.clientValues,
        role_types: objectives.consultingRoles,
        handle_hiring: objectives.hrManagement || "",
        client_types: objectives.clientTypes,
        project_volume: data.clientProjectVolume?.value,
        profile_picture: data.profile_picture,
        industry: data.industryFocus?.value,
        no_of_employees: data.clientProjectVolume?.value,
      });
    } else {
      Object.assign(submitData, {
        work_culture: objectives.culture || [],
        role_types: objectives.roleTypes || [],
        handle_hiring: objectives.hrManagement || "",
        name: data.name,
        industry: data.industry,
        hiring_frequency: data.frequency?.value,
        no_of_employees: data.employees?.value,
        ...(data.company_logo ? { company_logo: data.company_logo } : {}),
      });
    }

    submitBusiness(submitData);
  };

  const getStageTitle = () => {
    switch (stage) {
      case 1: return "About You";
      case 2: return "Objectives";
      case 3:
        return selectedRole === "independent_consultant"
          ? "Consultant Profile"
          : "Industry & Company";
      default: return "About You";
    }
  };

  const renderObjectivesForm = () => {
    switch (selectedRole) {
      case "company_hr":
        return (
          <CompanyHRObjectivesForm
            init={objectives}
            submit={goToStageThree}
            goBack={() => setStage(1)}
          />
        );
      case "hr_agency":
        return (
          <HRAgencyObjectivesForm
            init={objectives}
            submit={goToStageThree}
            goBack={() => setStage(1)}
          />
        );
      case "independent_consultant":
        return (
          <ConsultantObjectivesForm
            init={objectives}
            submit={goToStageThree}
            goBack={() => setStage(1)}
          />
        );
      default:
        return (
          <CompanyHRObjectivesForm
            init={objectives}
            submit={goToStageThree}
            goBack={() => setStage(1)}
          />
        );
    }
  };

  const renderFinalForm = () => {
    if (selectedRole === "independent_consultant") {
      return (
        <ConsultantProfileForm
          submit={submitAll}
          goBack={() => setStage(2)}
          handleImageUpload={handleImageUpload}
          image={formImage || image}
          imageURL={image}
          existingProfilePictureURL={existingProfilePictureURL}
          isSubmitting={isSubmittingBusiness}
        />
      );
    } else {
      return (
        <CompanyInfoForm
          submit={submitAll}
          goBack={() => setStage(2)}
          handleImageUpload={handleImageUpload}
          image={formImage || image}
          imageURL={image}
          existingLogoURL={existingLogoURL}
          isSubmitting={isSubmittingBusiness}
        />
      );
    }
  };

  return (
    <Modal
      position="centered"
      close={() => { }}
      show={show}
      contentClassName="sm:!p-12 !p-5 !max-h-[79.7vh] !max-w-[78.2vw] !w-[90vw] !overflow-y-auto !grid lg:grid-cols-2 gap-[4%] relative"
      dialogClassName="!h-[100dvh]"
    >
      <div className="h-fit pl-4 pr-[12%] lg:block hidden">
        <h1 className="text-[#012169] text-center font-bold text-[38px] leading-10">
          Welcome To Precision
        </h1>
        <p className="text-[#2B4682] text-center text-[19px] mx-auto w-[90%] mt-3.5 mb-10">
          Let's tailor your workspace so you get the most relevant insights.
        </p>
        <Rope className="mt-0.5 absolute left-1/2 -translate-x-1/2 top-[70px] ml-[40px]" />
        <Onboarding className="mt-0.5" />
      </div>

      <div>
        <ProgressSteps
          stage={stage}
          setStage={setStage}
          personal={personal}
          personalData={personalData}
        />

        <h1 className="text-[#012169] text-center sm:text-[38px] text-[26px] font-semibold mb-8">
          {getStageTitle()}
        </h1>

        {stage === 1 && (
          <PersonalInfoForm
            countryOptions={countryOptions}
            init={personal}
            submit={goToStageTwo}
            isSubmitting={isSubmittingPersonal}
          />
        )}

        {stage === 2 && renderObjectivesForm()}

        {stage === 3 && renderFinalForm()}
      </div>
    </Modal>
  );
};